import { pick } from 'lodash';

import type {
  CourseSession,
  CustomSessionOptionType,
  SessionOptionType,
} from 'bundles/enterprise-admin-drawers/components/types';
import type { CollectionType } from 'bundles/enterprise-collections/components/CollectionItem';
import type { Action } from 'bundles/naptimejs/client';
import type { EnterpriseAdminSessionSelectionOptionType } from 'bundles/naptimejs/resources/__generated__/EnterpriseProductConfigurationsV1';
import EnterpriseProductConfigurationOperationsV1 from 'bundles/naptimejs/resources/enterpriseProductConfigurationOperations.v1';

type CourseSessionUpdate = {
  typeName: 'course';
  definition: {
    courseId: string;
    sessionSelection: {
      migrateLearners: boolean;
      selectionType: {
        typeName: string;
        definition: Omit<CourseSession, 'id'> & {
          sessionId?: string;
          s12nId?: string;
          groupId?: string;
          sessionDates?: { startsAt?: number; endsAt?: number };
        };
      };
    };
    forCreditConfiguration?: {
      isSelectedForCredit: boolean;
      isSelectedToRequireAllItemCompletion: boolean;
    };
    enableAutoEnrollment?: boolean;
  };
};

type S12nSessionUpdate = {
  typeName: 'specialization';
  definition: {
    s12nId: string;
    forCreditConfiguration?: {
      isSelectedForCredit: boolean;
      isSelectedToRequireAllItemCompletion: boolean;
    };
    enableAutoEnrollment?: boolean;
  };
};

type SessionData = {
  selectedSession: CourseSession;
  selectedSessionOption: SessionOptionType;
  isSelectedForCredit?: boolean;
  isSelectedToRequireAllItemCompletion?: boolean;
  isAutoEnrollmentEnabled?: boolean;
  childCourseIdsandSessions?: { [courseId: string]: string | undefined };
  parentS12nId?: string;
};

export type SessionRecord = Record<string, SessionData>;
export type SessionRecordWithProductType = Record<string, SessionData & { isS12n?: boolean; parentS12nId?: string }>;

export type DataForProductConfigurationMutation = {
  productId: string;
  session: CourseSession;
  option: EnterpriseAdminSessionSelectionOptionType | CustomSessionOptionType;
  migrateLearners: boolean;
  isSelectedForCredit?: boolean;
  isSelectedToRequireAllItemCompletion?: boolean;
  shouldAutoEnroll?: boolean;
  parentS12nId?: string;
};
export type DataForProductConfigurationMutationWithType = DataForProductConfigurationMutation & {
  isS12n: boolean;
  parentS12nId?: string;
};

const genCourseUpdate = ({
  productId,
  session,
  option,
  migrateLearners,
  isSelectedForCredit,
  isSelectedToRequireAllItemCompletion,
  shouldAutoEnroll,
  parentS12nId,
}: DataForProductConfigurationMutation) => {
  let selectionTypeName: string;

  // If externally managed or if it's a course already with a sessionId being configured through a s12n
  const isExternallyManagedOption =
    option.typeName === 'externallyManagedSessionOption' || (parentS12nId && session.id);

  if (isExternallyManagedOption) {
    selectionTypeName = 'existingExternallyManagedSelection';
  } else {
    selectionTypeName = option.typeName.replace(/Option$/, 'Selection');
  }

  const update: CourseSessionUpdate = {
    typeName: 'course',
    definition: {
      courseId: productId,
      sessionSelection: {
        migrateLearners,
        selectionType: {
          typeName: selectionTypeName,
          definition: {
            ...pick(session, ['startsAt', 'endsAt', 'name']),
            ...(session.id && { sessionId: session.id }),
            ...(parentS12nId && option.typeName !== 'flexibleOption' && { s12nId: parentS12nId }),
          },
        },
      },
      ...(isSelectedForCredit !== undefined
        ? {
            forCreditConfiguration: {
              isSelectedForCredit,
              isSelectedToRequireAllItemCompletion: Boolean(isSelectedToRequireAllItemCompletion),
            },
          }
        : {}),
      ...(shouldAutoEnroll !== undefined
        ? {
            enableAutoEnrollment: shouldAutoEnroll,
          }
        : {}),
    },
  };

  if (option.typeName === 'privateSessionGroupOption') {
    update.definition.sessionSelection.selectionType.definition.groupId = option.definition.groupId;
  }

  if (isExternallyManagedOption) {
    update.definition.sessionSelection.selectionType.definition.sessionDates = {
      startsAt: update.definition.sessionSelection.selectionType.definition.startsAt,
      endsAt: update.definition.sessionSelection.selectionType.definition.endsAt,
    };
  }

  return update;
};

const genS12nUpdate = ({
  productId,
  isSelectedForCredit,
  shouldAutoEnroll,
  isSelectedToRequireAllItemCompletion,
}: DataForProductConfigurationMutation): S12nSessionUpdate => {
  return {
    typeName: 'specialization',
    definition: {
      s12nId: productId,
      ...(isSelectedForCredit !== undefined
        ? {
            forCreditConfiguration: {
              isSelectedForCredit,
              isSelectedToRequireAllItemCompletion: Boolean(isSelectedToRequireAllItemCompletion),
            },
          }
        : {}),
      ...(shouldAutoEnroll !== undefined
        ? {
            enableAutoEnrollment: shouldAutoEnroll,
          }
        : {}),
    },
  };
};

const genUpdate = ({ isS12n, ...data }: DataForProductConfigurationMutationWithType) => {
  return isS12n ? genS12nUpdate(data) : genCourseUpdate(data);
};

export function enterpriseProductConfigurationOperationsBulkUpdate(
  programId: string,
  data: DataForProductConfigurationMutationWithType[]
): Action {
  const updates = data.map((updateData) => genUpdate(updateData));
  return EnterpriseProductConfigurationOperationsV1.action('asyncBulkUpdate', { programId, updates });
}

export const getSessionMutation = (
  programId: string,
  sessions: SessionRecord,
  collections: CollectionType[],
  allowOrgForSpecializationConfiguration?: boolean
): Action | null => {
  if (!programId || !sessions) return null;

  const sessionsWithProductType = Object.entries(sessions).reduce((memo, [productId, session]) => {
    const isS12n = collections.some((collection) => collection.s12ns?.elements.some((s12n) => s12n?.id === productId));

    if (!isS12n && memo[productId]) return memo;

    // eslint-disable-next-line no-param-reassign
    memo[productId] = {
      ...session,
      isS12n,
    };

    if (allowOrgForSpecializationConfiguration && isS12n) {
      const allS12ns = collections
        .map((collection) => collection.s12ns?.elements ?? [])
        .reduce((acc, s12ns) => [...acc, ...s12ns], []);

      const childCourseIds = allS12ns.find((s12n) => s12n?.id === productId)?.courseIds;
      childCourseIds?.forEach((courseId) => {
        // eslint-disable-next-line no-param-reassign
        memo[courseId] = {
          isS12n: false,
          parentS12nId: productId,
          ...session,
          selectedSession: {
            ...session.selectedSession,
            courseId,
            // If the existing child session is being overwritten by a flexible session we do not want to include sessionId
            ...(session.childCourseIdsandSessions?.[courseId] &&
              session.selectedSessionOption.typeName !== 'flexibleOption' && {
                id: session.childCourseIdsandSessions[courseId],
              }),
          },
        };
      });
    }

    return memo;
  }, {} as SessionRecordWithProductType);

  const mutationData: DataForProductConfigurationMutationWithType[] = Object.values(sessionsWithProductType)
    .filter(
      (
        session
      ): session is Omit<SessionData, 'selectedSession'> & {
        selectedSession: Required<CourseSession>;
        isS12n: boolean;
      } => typeof session.selectedSession.courseId === 'string'
    )
    .map((session) => ({
      productId: session.selectedSession.courseId,
      session: session.selectedSession,
      option: session.selectedSessionOption,
      migrateLearners: false,
      isSelectedForCredit: session.isSelectedForCredit,
      isSelectedToRequireAllItemCompletion: session.isSelectedToRequireAllItemCompletion,
      shouldAutoEnroll: session.isAutoEnrollmentEnabled,
      isS12n: session.isS12n,
      ...(session.parentS12nId && { parentS12nId: session.parentS12nId }),
    }));

  if (mutationData.length === 0) return null;

  return enterpriseProductConfigurationOperationsBulkUpdate(programId, mutationData);
};
